import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Calendar,
  CalendarPlus,
  ArrowLeft,
  Loader2,
  User,
  Sparkles,
  Clock,
  Check,
  Search,
  FileText,
  Video,
  Phone,
  ExternalLink,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const API = "/api";

export default function AIAppointmentPage() {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appointmentHistory, setAppointmentHistory] = useState([]);
  const [mode, setMode] = useState("ai"); // ai or manual
  const [generating, setGenerating] = useState(false);
  const [selectedMeetingType, setSelectedMeetingType] = useState("product_demo");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [meetingNotes, setMeetingNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("history");
  const [aiRecommendations, setAiRecommendations] = useState([]);
  
  const token = localStorage.getItem("admin_token");

  const meetingTypes = [
    { id: "discovery", label: "Discovery Call", icon: Search, duration: 30 },
    { id: "product_demo", label: "Product Demo", icon: Video, duration: 45 },
    { id: "proposal", label: "Proposal Review", icon: FileText, duration: 60 },
    { id: "follow_up", label: "Follow-Up", icon: Phone, duration: 20 },
  ];

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchLead();
    fetchAppointmentHistory();
  }, [leadId, token]);

  useEffect(() => {
    if (mode === "ai" && lead) {
      generateAIRecommendations();
    }
  }, [mode, selectedMeetingType, lead]);

  const fetchLead = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLead(data);
      } else {
        toast.error("Lead not found");
        navigate(-1);
      }
    } catch (error) {
      toast.error("Failed to load lead");
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointmentHistory = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/appointment-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAppointmentHistory(data.appointment_history || []);
      }
    } catch (error) {
      console.error("Failed to fetch appointment history:", error);
    }
  };

  const generateAIRecommendations = () => {
    const selectedType = meetingTypes.find(t => t.id === selectedMeetingType);
    const duration = selectedType?.duration || 45;
    
    // Generate 3 AI recommended time slots
    const now = new Date();
    const recommendations = [];
    
    // First recommendation - best time aligned with prospect's stated preference
    const day1 = new Date(now);
    day1.setDate(day1.getDate() + 3);
    while (day1.getDay() === 0 || day1.getDay() === 6) day1.setDate(day1.getDate() + 1);
    recommendations.push({
      id: 1,
      day: day1.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
      rawDate: day1.toISOString().split('T')[0],
      time: "10:00 AM PST",
      score: 95,
      reason: "Aligns with prospect's stated best time (10-12 PST). High engagement day based on email opens."
    });
    
    // Second recommendation
    const day2 = new Date(day1);
    day2.setDate(day2.getDate() + 1);
    while (day2.getDay() === 0 || day2.getDay() === 6) day2.setDate(day2.getDate() + 1);
    recommendations.push({
      id: 2,
      day: day2.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
      rawDate: day2.toISOString().split('T')[0],
      time: "11:00 AM PST",
      score: 88,
      reason: "Mid-week momentum. Last contact was 3 days ago — good re-engagement window."
    });
    
    // Third recommendation
    const day3 = new Date(day2);
    day3.setDate(day3.getDate() + 3);
    while (day3.getDay() === 0 || day3.getDay() === 6) day3.setDate(day3.getDate() + 1);
    recommendations.push({
      id: 3,
      day: day3.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
      rawDate: day3.toISOString().split('T')[0],
      time: "10:30 AM PST",
      score: 80,
      reason: "Start of new week — prospects are often planning-focused and receptive."
    });
    
    setAiRecommendations(recommendations);
  };

  const bookAppointment = async () => {
    if (!selectedTimeSlot && mode === "ai") {
      toast.error("Please select a time slot");
      return;
    }

    setSaving(true);
    try {
      const selectedType = meetingTypes.find(t => t.id === selectedMeetingType);
      const slot = aiRecommendations.find(r => r.id === selectedTimeSlot);
      
      const response = await fetch(`${API}/admin/leads/${leadId}/appointment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          appointment_date: slot?.rawDate || new Date().toISOString().split('T')[0],
          appointment_time: slot?.time || "10:00 AM",
          duration: selectedType?.duration || 45,
          type: selectedMeetingType,
          notes: meetingNotes,
          meeting_type_label: selectedType?.label
        })
      });

      if (response.ok) {
        toast.success("Appointment booked!");
        fetchAppointmentHistory();
        setSelectedTimeSlot(null);
        setMeetingNotes("");
      } else {
        toast.error("Failed to book appointment");
      }
    } catch (error) {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  const formatHistoryDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Calculate stats
  const getTotalBooked = () => appointmentHistory.length;
  const getCompleted = () => appointmentHistory.filter(a => a.status === "completed").length;
  const getUpcoming = () => appointmentHistory.filter(a => {
    const apptDate = new Date(a.date);
    return apptDate > new Date() && a.status !== "cancelled";
  }).length;
  const getShowRate = () => {
    const completed = getCompleted();
    const total = getTotalBooked();
    if (total === 0) return 100;
    return Math.round((completed / total) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  const selectedType = meetingTypes.find(t => t.id === selectedMeetingType);

  return (
    <div className="min-h-screen bg-[#050505] p-6" data-testid="ai-appointment-page">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Main Content */}
          <div className="col-span-8 space-y-6">
            {/* Back Button */}
            <button
              onClick={() => navigate(`/admin/leads/${leadId}`)}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>← Back to {lead?.first_name}'s Profile</span>
            </button>

            {/* Lead Info Card */}
            <div className="bg-[#0d1a0d] rounded-2xl border border-amber-500/20 p-6">
              <div className="flex items-start gap-6">
                {/* Avatar and Lead Info */}
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-amber-500/20 flex items-center justify-center">
                    <User className="w-8 h-8 text-amber-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="text-xl font-bold text-white">{lead?.first_name} {lead?.last_name}</h2>
                      {lead?.ai_score && (
                        <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs font-bold rounded border border-amber-500/30">
                          Score {lead.ai_score}
                        </span>
                      )}
                    </div>
                    <p className="text-zinc-400 text-sm">{lead?.job_title || "Contact"} @ {lead?.company_name || "Unknown"}</p>
                    <p className="text-amber-400/80 text-xs mt-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Best time: 10 AM - 12 PM PST
                    </p>
                  </div>
                </div>

                {/* Context Card */}
                <div className="flex-1 bg-[#0a0a0a] rounded-xl p-4 border border-zinc-800">
                  <p className="text-[10px] text-zinc-500 uppercase font-semibold tracking-widest mb-2">Context</p>
                  <p className="text-sm text-zinc-300">
                    {lead?.notes || lead?.pain_points || "Interested in AI CRM. Budget ~$50K. Decision by Q2."}
                  </p>
                </div>
              </div>
            </div>

            {/* Meeting Type Selection */}
            <div>
              <p className="text-[10px] text-zinc-500 uppercase font-semibold tracking-widest mb-4">Meeting Type</p>
              <div className="grid grid-cols-4 gap-3">
                {meetingTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => setSelectedMeetingType(type.id)}
                      data-testid={`meeting-type-${type.id}`}
                      className={`p-4 rounded-xl border-2 transition-all text-center ${
                        selectedMeetingType === type.id
                          ? "border-amber-500 bg-amber-500/10"
                          : "border-zinc-800 bg-zinc-900/50 hover:border-zinc-700"
                      }`}
                    >
                      <Icon className={`w-5 h-5 mx-auto mb-2 ${selectedMeetingType === type.id ? "text-amber-400" : "text-zinc-500"}`} />
                      <p className={`text-sm font-medium ${selectedMeetingType === type.id ? "text-amber-400" : "text-white"}`}>
                        {type.label}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">{type.duration} min</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Scheduling Mode */}
            <div>
              <p className="text-[10px] text-zinc-500 uppercase font-semibold tracking-widest mb-4">Scheduling Mode</p>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setMode("ai")}
                  data-testid="mode-ai"
                  className={`p-5 rounded-xl border-2 transition-all text-left ${
                    mode === "ai"
                      ? "border-amber-500 bg-amber-500/10"
                      : "border-zinc-800 bg-zinc-900/50 hover:border-zinc-700"
                  }`}
                >
                  <Sparkles className={`w-6 h-6 mb-3 ${mode === "ai" ? "text-amber-400" : "text-zinc-500"}`} />
                  <p className={`font-semibold ${mode === "ai" ? "text-amber-400" : "text-white"}`}>AI Suggest</p>
                  <p className="text-xs text-zinc-500 mt-1">ZOEY recommends optimal times based on timezone, past engagement, and stated availability.</p>
                </button>
                
                <button
                  onClick={() => setMode("manual")}
                  data-testid="mode-manual"
                  className={`p-5 rounded-xl border-2 transition-all text-left ${
                    mode === "manual"
                      ? "border-amber-500 bg-amber-500/10"
                      : "border-zinc-800 bg-zinc-900/50 hover:border-zinc-700"
                  }`}
                >
                  <Calendar className={`w-6 h-6 mb-3 ${mode === "manual" ? "text-amber-400" : "text-zinc-500"}`} />
                  <p className={`font-semibold ${mode === "manual" ? "text-amber-400" : "text-white"}`}>Manual Schedule</p>
                  <p className="text-xs text-zinc-500 mt-1">Pick your own date and time from the calendar.</p>
                </button>
              </div>
            </div>

            {/* AI Recommendations */}
            {mode === "ai" && (
              <div>
                <p className="text-[10px] text-zinc-500 uppercase font-semibold tracking-widest mb-4">
                  AI Recommendations — {selectedType?.label} ({selectedType?.duration} min)
                </p>
                <div className="space-y-3">
                  {aiRecommendations.map((slot) => (
                    <button
                      key={slot.id}
                      onClick={() => setSelectedTimeSlot(slot.id)}
                      data-testid={`time-slot-${slot.id}`}
                      className={`w-full p-5 rounded-xl border-2 transition-all text-left flex items-center justify-between ${
                        selectedTimeSlot === slot.id
                          ? "border-amber-500 bg-amber-500/10"
                          : "border-zinc-800 bg-zinc-900/50 hover:border-zinc-700"
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <p className="font-semibold text-white">{slot.day}</p>
                          <p className="text-amber-400 font-medium">{slot.time}</p>
                        </div>
                        <p className="text-xs text-zinc-500">{slot.reason}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-amber-400">{slot.score}</p>
                          <p className="text-[10px] text-zinc-500 uppercase">AI Score</p>
                        </div>
                        <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${
                          selectedTimeSlot === slot.id
                            ? "border-amber-500 bg-amber-500"
                            : "border-zinc-700"
                        }`}>
                          {selectedTimeSlot === slot.id && <Check className="w-5 h-5 text-black" />}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Meeting Notes */}
            <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 p-5">
              <p className="text-[10px] text-zinc-500 uppercase font-semibold tracking-widest mb-3">Meeting Notes (optional)</p>
              <textarea
                value={meetingNotes}
                onChange={(e) => setMeetingNotes(e.target.value)}
                placeholder="Add context for the product team — what should they know before this call?"
                rows={3}
                data-testid="meeting-notes"
                className="w-full bg-transparent text-zinc-300 text-sm outline-none resize-none placeholder-zinc-600"
              />
            </div>

            {/* Book Button */}
            <Button
              onClick={bookAppointment}
              disabled={saving || (mode === "ai" && !selectedTimeSlot)}
              data-testid="book-appointment-btn"
              className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30 px-8 py-5 rounded-xl text-sm font-medium"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Book Appointment
            </Button>
          </div>

          {/* Right Column - Header, Stats, History */}
          <div className="col-span-4 space-y-6">
            {/* Page Header */}
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500">
                <CalendarPlus className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">Schedule Appointment</h1>
                <p className="text-xs text-zinc-500">Book meetings with {lead?.first_name} {lead?.last_name}</p>
              </div>
            </div>

            {/* Statistics */}
            <div>
              <p className="text-[10px] text-zinc-500 uppercase font-semibold tracking-widest mb-4">Statistics</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-zinc-900/80 rounded-xl p-4 text-center border border-zinc-800">
                  <p className="text-2xl font-bold text-cyan-400">{getTotalBooked()}</p>
                  <p className="text-[10px] text-zinc-500 mt-1">Total Booked</p>
                </div>
                <div className="bg-zinc-900/80 rounded-xl p-4 text-center border border-zinc-800">
                  <p className="text-2xl font-bold text-green-400">{getCompleted()}</p>
                  <p className="text-[10px] text-zinc-500 mt-1">Completed</p>
                </div>
                <div className="bg-zinc-900/80 rounded-xl p-4 text-center border border-zinc-800">
                  <p className="text-2xl font-bold text-cyan-400">{getUpcoming()}</p>
                  <p className="text-[10px] text-zinc-500 mt-1">Upcoming</p>
                </div>
                <div className="bg-zinc-900/80 rounded-xl p-4 text-center border border-zinc-800">
                  <p className="text-2xl font-bold text-green-400">{getShowRate()}%</p>
                  <p className="text-[10px] text-zinc-500 mt-1">Show Rate</p>
                </div>
              </div>
            </div>

            {/* Tabs: History / Tips */}
            <div>
              <div className="flex gap-6 border-b border-zinc-800 mb-4">
                <button
                  onClick={() => setActiveTab("history")}
                  className={`pb-3 text-sm font-medium transition-colors ${
                    activeTab === "history"
                      ? "text-amber-400 border-b-2 border-amber-400"
                      : "text-zinc-500 hover:text-white"
                  }`}
                >
                  History
                </button>
                <button
                  onClick={() => setActiveTab("tips")}
                  className={`pb-3 text-sm font-medium transition-colors ${
                    activeTab === "tips"
                      ? "text-amber-400 border-b-2 border-amber-400"
                      : "text-zinc-500 hover:text-white"
                  }`}
                >
                  Tips
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === "history" && (
                <div className="space-y-3 max-h-[350px] overflow-y-auto">
                  {appointmentHistory.length > 0 ? (
                    appointmentHistory.map((appt, idx) => (
                      <div 
                        key={idx}
                        className="bg-zinc-900/60 rounded-xl p-4 border border-zinc-800/50"
                        data-testid={`history-item-${idx}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-zinc-500">{formatHistoryDate(appt.date)}</p>
                          <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                            appt.status === "completed" 
                              ? "bg-green-500/20 text-green-400"
                              : appt.status === "cancelled"
                              ? "bg-red-500/20 text-red-400"
                              : "bg-amber-500/20 text-amber-400"
                          }`}>
                            {appt.status === "completed" ? "Completed" : appt.status === "cancelled" ? "Cancelled" : "Scheduled"}
                          </span>
                        </div>
                        <p className="text-amber-400 font-medium text-sm">
                          {appt.meeting_type_label || appt.type || "Meeting"} · {appt.time}
                        </p>
                        {appt.notes && (
                          <p className="text-xs text-zinc-500 mt-1">{appt.notes}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 bg-zinc-900/30 rounded-xl border border-zinc-800/30">
                      <CalendarPlus className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
                      <p className="text-xs text-zinc-600">No appointments yet</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "tips" && (
                <div className="space-y-3">
                  <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20">
                    <p className="text-sm text-amber-300">
                      <span className="font-medium text-amber-400">Best time:</span> Schedule during the prospect's stated availability for higher show rates.
                    </p>
                  </div>
                  <div className="bg-zinc-900/60 rounded-xl p-4">
                    <p className="text-sm text-zinc-300">
                      <span className="font-medium text-white">AI suggestions:</span> ZOEY analyzes past engagement patterns to recommend optimal times.
                    </p>
                  </div>
                  <div className="bg-zinc-900/60 rounded-xl p-4">
                    <p className="text-sm text-zinc-300">
                      <span className="font-medium text-white">Meeting notes:</span> Add context so the team is prepared for the call.
                    </p>
                  </div>
                  <div className="bg-zinc-900/60 rounded-xl p-4">
                    <p className="text-sm text-zinc-300">
                      <span className="font-medium text-white">Follow-up:</span> Always schedule a follow-up before ending the current meeting.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
